import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { TeamtailorForm } from '~/components/features/integrations/teamtailor/teamtailor-form';
import { PageContainer } from '~/components/layouts/page-container';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { resolveOrgId } from '~/lib/slug-resolver';
import { atsConfigQueryOptions } from '~/queries/integrations.queries';

export const Route = createFileRoute('/_authenticated/org/$orgSlug/integrations/teamtailor/')({
  loader: ({ context, params }) => {
    const orgId = resolveOrgId(context.queryClient, params.orgSlug);
    if (orgId) {
      void context.queryClient.ensureQueryData(atsConfigQueryOptions(orgId));
    }
  },
  component: TeamtailorPage
});

function TeamtailorPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('integrations.ats.providers.teamtailor'));
  const { orgSlug } = Route.useParams();
  const { orgId } = useSlugIds(orgSlug);
  const { data } = useQuery({ ...atsConfigQueryOptions(orgId), enabled: !!orgId });

  const config = data?.config?.provider === 'teamtailor' ? data.config : null;

  return (
    <PageContainer
      title="Teamtailor"
      subtitle={t('integrations.ats.description')}
      actions={
        <img
          src="/integrations/teamtailor.png"
          alt="Teamtailor"
          className="w-24 h-12 rounded-2xl object-contain"
        />
      }
      breadcrumbs={[
        { label: t('integrations.title'), href: `/org/${orgSlug}/integrations` },
        { label: 'Teamtailor' }
      ]}
    >
      <TeamtailorForm orgId={orgId} config={config} />
    </PageContainer>
  );
}
